import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, X } from 'lucide-react';
import { TaskForm } from './TaskForm';
import type { TaskItem } from '../../../types/topic';
import { logger } from '../../../utils/logger';
import { listCollaborators } from '../../projects/services/projectCollaboratorService';
import { cacheUser, getCachedUser } from '../../../utils/userCache';
import { useAvailableTags } from '../../../hooks/queries/useTasks';
import { useViewport } from '../../../contexts/ViewportContext';
import { MobileFullScreenModal } from '../../ui/MobileFullScreenModal';

interface TaskModalProps {
  task: TaskItem;
  projectId?: string;
  onClose: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  focusField?: 'deadline' | 'title' | 'description' | 'tags';
  hasCollaborators?: boolean;
}

export function TaskModal({
  task,
  projectId,
  onClose,
  onUpdateTask,
  _focusField,
  hasCollaborators = false,
}: TaskModalProps) {
  const [isEdited, setIsEdited] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState<TaskItem | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();
  
  // Get available tags for this topic using React Query
  const topicId = (task as any).topicId;
  const topicTags = useAvailableTags(topicId);

  // Function to check if the task has been modified
  const checkForChanges = (updatedTask: TaskItem) => {
    // Log the original vs updated task data for debugging
    logger.component('TaskModal comparing changes', {
      taskId: task.id,
      originalDeadline: task.deadline,
      updatedDeadline: updatedTask.deadline,
    });

    // Normalize values for accurate comparison
    const normalizedOrigTask = {
      title: task.title || '',
      description: task.description || null,
      deadline: task.deadline || null,
      tags: task.tags || null,
      assigned_to: (task as any).assignedTo || null,
    };

    const normalizedUpdatedTask = {
      title: updatedTask.title || '',
      description: updatedTask.description || null,
      deadline: updatedTask.deadline || null,
      tags: updatedTask.tags || null,
      assigned_to: updatedTask.assigned_to || null,
    };

    // Safely compare dates by handling ISO string dates
    const isSameDeadline = () => {
      // If both are null/undefined, they're the same
      if (!normalizedOrigTask.deadline && !normalizedUpdatedTask.deadline) return true;
      // If one is null and the other isn't, they're different
      if (!normalizedOrigTask.deadline || !normalizedUpdatedTask.deadline) return false;

      // Instead of string comparison, parse both into Date objects and compare the date parts only
      try {
        const origDate = new Date(normalizedOrigTask.deadline);
        const updatedDate = new Date(normalizedUpdatedTask.deadline);

        // Compare only the year, month, and day
        const isSame =
          origDate.getFullYear() === updatedDate.getFullYear() &&
          origDate.getMonth() === updatedDate.getMonth() &&
          origDate.getDate() === updatedDate.getDate();

        // Log date comparison for debugging
        logger.component('TaskModal deadline comparison', {
          taskId: task.id,
          orig: normalizedOrigTask.deadline,
          updated: normalizedUpdatedTask.deadline,
          isSame,
        });

        return isSame;
      } catch (error) {
        // If there's any parsing error, fall back to string comparison (just in case)
        logger.error('Error comparing dates', { error });
        return normalizedOrigTask.deadline === normalizedUpdatedTask.deadline;
      }
    };

    const titleChanged = normalizedUpdatedTask.title !== normalizedOrigTask.title;
    const descriptionChanged = normalizedUpdatedTask.description !== normalizedOrigTask.description;
    const deadlineChanged = !isSameDeadline();
    const tagsChanged =
      JSON.stringify(normalizedUpdatedTask.tags) !== JSON.stringify(normalizedOrigTask.tags);
    const assigneeChanged = normalizedUpdatedTask.assigned_to !== normalizedOrigTask.assigned_to;

    const hasChanges =
      titleChanged || descriptionChanged || deadlineChanged || tagsChanged || assigneeChanged;

    // Only update state if the edited status has actually changed
    // This prevents unnecessary re-renders
    if (hasChanges && !isEdited) {
      logger.component('TaskModal detected changes', {
        taskId: task.id,
        titleChanged,
        descriptionChanged,
        deadlineChanged,
        tagsChanged,
        assigneeChanged,
      });
      setIsEdited(true);
      setFormData(updatedTask);
    } else if (!hasChanges && isEdited) {
      logger.component('TaskModal no changes detected');
      setIsEdited(false);
      setFormData(null);
    } else if (hasChanges) {
      // Just update the form data without changing edited status
      setFormData(updatedTask);
    }
  };

  // Handle clicking outside the modal
  const handleBackdropClick = () => {
    if (isEdited) {
      // If there are unsaved changes, show confirmation dialog
      setShowConfirmation(true);
    } else {
      // If no changes, just close
      onClose();
    }
  };

  // Handle save button in the confirmation dialog
  const handleConfirmSave = () => {
    if (formData) {
      onUpdateTask(task.id, formData);
      setShowConfirmation(false);
      onClose();
    }
  };

  // Handle discard changes in the confirmation dialog
  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    onClose();
  };

  // Close confirmation without any action
  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Handler for the actual form submission
  const handleFormSubmit = (updatedTask: TaskItem) => {
    try {
      // Compare the original task with updated task to determine what changed
      const titleChanged = task.title !== updatedTask.title;
      const descriptionChanged = task.description !== updatedTask.description;
      const deadlineChanged = task.deadline !== updatedTask.deadline;
      const tagsChanged = JSON.stringify(task.tags) !== JSON.stringify(updatedTask.tags);
      const assigneeChanged = (task as any).assignedTo !== updatedTask.assigned_to;

      // Only update if something actually changed
      if (titleChanged || descriptionChanged || deadlineChanged || tagsChanged || assigneeChanged) {
        // Create an updates object with only the changed fields
        const updates: Partial<TaskItem> = {};

        if (titleChanged) updates.title = updatedTask.title;
        if (descriptionChanged) updates.description = updatedTask.description;
        if (deadlineChanged) updates.deadline = updatedTask.deadline;
        if (tagsChanged) updates.tags = updatedTask.tags;
        if (assigneeChanged) updates.assigned_to = updatedTask.assigned_to;

        // Send only the changed fields to store
        onUpdateTask(task.id, updates);
      }

      // Close modal
      onClose();
    } catch (error) {
      logger.error('TaskModal error updating task', { error });
    }
  };

  // Load the tag suggestions immediately when modal opens
  // Tags are now fetched automatically by React Query via useAvailableTags

  // Pre-fetch collaborator data when the modal opens if the task has an assigned user
  useEffect(() => {
    const fetchAssignedUserData = async () => {
      // Use FormattedTask format with assignedTo instead of assigned_to
      const assignedTo = (task as any).assignedTo;
      if (!assignedTo || !projectId) return;

      // Check if the user is already in the cache
      const cachedUser = getCachedUser(assignedTo);

      // If already cached, no need to fetch
      if (cachedUser) {
        return;
      }

      // User not in cache, fetch collaborators to get their data
      try {
        const collaborators = await listCollaborators(projectId);

        // Find the assigned user in the collaborators list
        const assignedCollaborator = collaborators.find(c => c.user_id === assignedTo);

        if (assignedCollaborator?.users_with_details) {
          // Cache the user's data
          cacheUser(assignedTo, {
            id: assignedTo,
            email: assignedCollaborator.users_with_details.email || '',
            display_name: assignedCollaborator.users_with_details.display_name || '',
            avatar_url: assignedCollaborator.users_with_details.avatar_url || undefined,
          });
        }
      } catch (error) {
        logger.error('Failed to fetch collaborators', {
          error: error instanceof Error ? error.message : String(error),
          projectId,
        });
      }
    };

    // Execute the fetch
    fetchAssignedUserData();
  }, [(task as any).assignedTo, projectId]);

  // Add event listener for escape key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showConfirmation) {
          // If confirmation dialog is open, just close it
          setShowConfirmation(false);
        } else if (isEdited) {
          // If there are unsaved changes, show confirmation dialog
          setShowConfirmation(true);
        } else {
          // Otherwise, just close the modal
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEscKey);

    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isEdited, showConfirmation, onClose]);

  // Helper function to render the confirmation modal
  const renderConfirmationModal = () => {
    return createPortal(
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
        {/* Invisible layer for closing confirmation when clicking outside */}
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={handleCloseConfirmation}
          aria-label="Close confirmation"
        />

        {/* Confirmation dialog content */}
        <div
          className="z-[70] bg-white rounded-lg shadow-xl w-full max-w-sm m-4 relative"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-5">
            {/* Header with title and close button */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Unsaved Changes</h3>
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={handleCloseConfirmation}
              >
                <span className="sr-only">Close</span>
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Message */}
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                You have unsaved changes. Do you want to save them before closing?
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={handleConfirmCancel}
              >
                Don't Save
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={handleConfirmSave}
              >
                <CheckCircle className="h-4 w-4 mr-1.5" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // For mobile, use the MobileFullScreenModal component
  if (isMobile) {
    return createPortal(
      <>
        <MobileFullScreenModal
          isOpen={true}
          onClose={handleBackdropClick}
          title="Edit Task"
          footerContent={
            formData && (
              <div className="flex gap-3 justify-end">
                <button
                  onClick={onClose}
                  className="py-2.5 px-4 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => formData && handleFormSubmit(formData)}
                  className="py-2.5 px-4 text-white bg-blue-500 rounded-md hover:bg-blue-600"
                  disabled={!isEdited}
                >
                  Save Changes
                </button>
              </div>
            )
          }
        >
          <div className="p-4">
            <TaskForm
              isEditMode={true}
              projectId={projectId}
              task={task}
              onSubmit={handleFormSubmit}
              onCancel={onClose}
              onChange={checkForChanges}
              hasCollaborators={hasCollaborators}
              availableTags={topicTags.length > 0 ? topicTags : undefined}
              hideButtons={true} /* Hide form buttons since we use the modal footer */
            />
          </div>
        </MobileFullScreenModal>

        {/* Confirmation Modal for Unsaved Changes - even on mobile this can be a simple modal */}
        {showConfirmation && renderConfirmationModal()}
      </>,
      document.body
    );
  }

  // For desktop, use the original modal design
  return createPortal(
    <>
      {/* Unified backdrop and modal container */}
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        {/* Area outside modal, full-screen clickable backdrop */}
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={handleBackdropClick}
          aria-label="Close modal"
        />

        {/* Modal content */}
        <div
          ref={modalRef}
          className="z-50 bg-white rounded-lg shadow-xl w-full max-w-md m-4 relative"
          onClick={e => e.stopPropagation()}
        >
          {/* Content */}
          <div className="p-6">
            <TaskForm
              isEditMode={true}
              projectId={projectId}
              task={task}
              onSubmit={handleFormSubmit}
              onCancel={onClose}
              onChange={checkForChanges}
              hasCollaborators={hasCollaborators}
              availableTags={topicTags.length > 0 ? topicTags : undefined}
            />
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Unsaved Changes */}
      {showConfirmation && renderConfirmationModal()}
    </>,
    document.body
  );
}
